import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import './_styles_/HolidayYear.css';
import ResetFieldButton from '../buttons/ResetFieldButton';
import HolidayYearTable from './HolidayYearTable';
import HolidayYearCreateHoliday from './HolidayYearCreateHoliday';
import {
    holidayDatesType,
    holidayYearsType,
} from '../../types';
import { createTemporaryId } from '../../utils/tableRecords';
import HolidayYearCopyHolidays from './HolidayYearCopyHolidays';
import useEffectOnUpdate from '../../hooks/useEffectOnUpdate';
import Privileges from '../../utils/Privileges';
import { Privileged } from '../../hocs/withPrivileges';

HolidayYear.propTypes = {
    year: PropTypes.number,
    holidays: holidayDatesType.isRequired,
    availableHolidayYears: holidayYearsType.isRequired,
    onHolidayYearChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    isChanged: PropTypes.bool.isRequired,
    validator: PropTypes.func.isRequired,
    doResetComponentsAfterModification: PropTypes.bool,
    isRequired: PropTypes.bool,
};

HolidayYear.defaultProps = {
    isRequired: false,
    year: null,
    doResetComponentsAfterModification: false,
};

function HolidayYear(props) {
    const {
        year,
        holidays,
        availableHolidayYears,
        onHolidayYearChange,
        isChanged,
        isRequired,
        onReset,
        validator,
        doResetComponentsAfterModification,
    } = props;
    const { t } = useTranslation();
    const [doReset, setDoReset] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const onChange = (newHolidays) => {
        onHolidayYearChange(newHolidays);
        setDoReset(false);
        validator(newHolidays)
            .then(() => setValidationError(null))
            .catch((e) => setValidationError(e));
    };
    const onResetInternal = () => {
        setDoReset(true);
        onReset();
    };
    const onHolidayYearDateCreate = (name, dateWithoutYear) => {
        onChange([...holidays, {
            id: createTemporaryId(),
            name,
            dateWithoutYear,
            isValid: true,
        }]);
    };
    const onHolidayYearDateDelete = (id) => {
        onChange(holidays.filter((holiday) => holiday.id !== id));
    };
    const onHolidayYearDateChange = (newDataToChange, isValid) => {
        onChange(holidays.map((holiday) => {
            let result = holiday;
            if (holiday.id === newDataToChange.id) {
                result = {
                    id: newDataToChange.id,
                    name: newDataToChange.name,
                    dateWithoutYear: newDataToChange.dateWithoutYear,
                    isValid,
                };
            }
            return result;
        }));
    };
    useEffectOnUpdate(() => {
        validator(holidays)
            .then(() => setValidationError(null))
            .catch((e) => setValidationError(e));
    }, [holidays]);
    return (
        <div className={`holiday-year field${isChanged ? ' modified' : ''}${(isRequired && !holidays.length) ? ' required' : ''}`}>
            <div className="holiday-year-field">
                <div className="field--key">
                    {t('holidays.table-title')}
                    <div className="holiday-year--validations">
                        {validationError && (
                            <div className="validation-error holiday-year--error">
                                {validationError}
                            </div>
                        )}
                    </div>
                </div>
                <Privileged sufficientPrivileges={[Privileges.HOLIDAY_EDIT]}>
                    <div className="holiday-year--add-holidays">
                        <HolidayYearCreateHoliday
                            year={year}
                            holidays={holidays}
                            onHolidayYearDateCreate={onHolidayYearDateCreate}
                        />
                        {availableHolidayYears.length > 0 && (
                            <HolidayYearCopyHolidays
                                availableHolidayYears={availableHolidayYears}
                                onCopyHolidays={onChange}
                            />
                        )}
                    </div>
                </Privileged>
                {holidays.length > 0 && (
                    <HolidayYearTable
                        year={year}
                        holidays={holidays}
                        onHolidayYearDateDelete={onHolidayYearDateDelete}
                        onHolidayYearDateChange={onHolidayYearDateChange}
                        doReset={doReset}
                        doResetComponentsAfterModification={doResetComponentsAfterModification}
                    />
                )}
            </div>
            {isChanged && onReset && <ResetFieldButton onReset={onResetInternal} />}
        </div>
    );
}

export default HolidayYear;
